import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'flight380_ai_session_id';

const AIAgentContext = createContext(null);

function ensureSessionId() {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const sid =
      (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
      `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sid);
    return sid;
  } catch (_) {
    return `s-${Date.now()}`;
  }
}

const GREETING_BY_CONTEXT = {
  homepage:
    "👋 Salaam! I'm Ami — Flight380's AI travel concierge. Ask me anything about Umrah packages, hotels near the Haram, visas, or pricing.",
  umrah:
    "I'm here to help you build the perfect Umrah package. Tell me your dates and budget, and I'll suggest a tier (Brown / Silver / Gold).",
  hotel:
    "Looking for a hotel near the Haram? Tell me your budget per night and how close you'd like to be — I'll recommend the best fit.",
  checkout:
    "Almost there! If you have any last-minute questions about cancellation, baggage or the booking flow, just ask.",
};

export const AIAgentProvider = ({ children }) => {
  // The widget is mounted globally; state for whether it's open and which context
  // it should act as is shared across the tree.
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState('homepage');
  const [state, setState] = useState({}); // visitor's snapshot (cart/builder state)
  // messages: { role: 'user' | 'assistant', content: string, ts: number }
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [leadSignal, setLeadSignal] = useState('cold');
  const [unread, setUnread] = useState(0);
  const sessionIdRef = useRef(ensureSessionId());

  // Seed greeting whenever context changes
  useEffect(() => {
    const greet = GREETING_BY_CONTEXT[context] || GREETING_BY_CONTEXT.homepage;
    setMessages((prev) => {
      if (prev.length === 0) return [{ role: 'assistant', content: greet, ts: Date.now() }];
      // If switching contexts mid-session, prepend a soft context-switch note
      const lastIsContextSwitch = prev[prev.length - 1]?.meta === 'context-switch';
      if (lastIsContextSwitch) return prev;
      return [...prev, { role: 'assistant', content: greet, ts: Date.now(), meta: 'context-switch' }];
    });
  }, [context]);

  const openAgent = useCallback((nextContext, snapshot) => {
    if (nextContext) setContext(nextContext);
    if (snapshot) setState(snapshot);
    setIsOpen(true);
    setUnread(0);
  }, []);
  const closeAgent = useCallback(() => setIsOpen(false), []);
  const toggleAgent = useCallback(() => setIsOpen((v) => !v), []);

  const updateState = useCallback((snapshot) => {
    setState((prev) => ({ ...prev, ...(snapshot || {}) }));
  }, []);
  const switchContext = useCallback((next) => {
    if (next && next !== context) setContext(next);
  }, [context]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed || pending) return;
      setPending(true);
      setMessages((prev) => [...prev, { role: 'user', content: trimmed, ts: Date.now() }]);

      try {
        const res = await fetch(`${API_URL}/api/ai-agent/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            context,
            message: trimmed,
            state,
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        const reply = data?.reply || "Sorry — I'm having trouble right now.";
        const packages = data?.packages || null;
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: reply, ts: Date.now(), packages },
        ]);
        if (data?.lead_signal === 'hot') setLeadSignal('hot');
        if (!isOpen) setUnread((u) => u + 1);
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: "I couldn't reach our servers. Please try again in a moment.", ts: Date.now() },
        ]);
      } finally {
        setPending(false);
      }
    },
    [API_URL, context, isOpen, pending, state],
  );

  const submitLead = useCallback(
    async ({ name, email, phone, message }) => {
      try {
        const res = await fetch(`${API_URL}/api/ai-agent/lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            context,
            name,
            email,
            phone,
            message,
            state,
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        return { success: true, lead: data?.lead };
      } catch (e) {
        return { success: false, error: e?.message || 'Failed to submit' };
      }
    },
    [API_URL, context, state],
  );

  const resetConversation = useCallback(() => {
    setMessages([]);
    setLeadSignal('cold');
  }, []);

  const value = useMemo(
    () => ({
      isOpen, openAgent, closeAgent, toggleAgent,
      context, switchContext,
      state, updateState,
      messages, pending,
      sendMessage, submitLead, resetConversation,
      leadSignal, unread,
      sessionId: sessionIdRef.current,
    }),
    [isOpen, openAgent, closeAgent, toggleAgent, context, switchContext, state, updateState,
      messages, pending, sendMessage, submitLead, resetConversation, leadSignal, unread],
  );

  return <AIAgentContext.Provider value={value}>{children}</AIAgentContext.Provider>;
};

export const useAIAgent = () => {
  const ctx = useContext(AIAgentContext);
  if (!ctx) throw new Error('useAIAgent must be used inside <AIAgentProvider>');
  return ctx;
};

export default AIAgentContext;
